/**
 * eFCR and bFCR per lot (v2.2 findings.md #215).
 *
 * eFCR (economic) = feed ÷ net production, where
 *   net production = harvested kg + biomass at the end − biomass at the start
 * (the start is the lot's stocked biomass, or its biomass when the report
 * period starts). Fish that died or went missing are a loss, so they raise
 * eFCR — this is the FCR the certification standards ask for.
 *
 * bFCR (biological) = feed ÷ (net production + biomass of the dead fish):
 * what the fish actually converted, counting the weight they had gained
 * before they died.
 *
 * Pure and side-effect free.
 */
import type { FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { rollupWeightSamplesByLotDay } from './dailyWeightRollup'
import { toLocalCalendarDayKey } from './dateNormalization'
import { farmTimeZone } from './farmTime'

const parseNumber = (raw: unknown): number | null => {
  const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replaceAll(',', '.').trim())
  return Number.isFinite(num) ? num : null
}

export const computeNetProductionKg = (args: {
  harvestKg: number
  endBiomassKg: number | null
  startBiomassKg: number | null
}): number | null => {
  if (args.startBiomassKg === null || args.endBiomassKg === null) return null
  return args.harvestKg + args.endBiomassKg - args.startBiomassKg
}

/** feed ÷ net production; null when feed or net production is not positive. */
export const computeEconomicFcr = (feedKg: number, netProductionKg: number | null): number | null => {
  if (!(feedKg > 0) || netProductionKg === null || !(netProductionKg > 0)) return null
  return feedKg / netProductionKg
}

/** feed ÷ (net production + dead biomass); null when either side is not positive. */
export const computeBiologicalFcr = (
  feedKg: number,
  netProductionKg: number | null,
  deadBiomassKg: number | null
): number | null => {
  if (netProductionKg === null || deadBiomassKg === null) return null
  return computeEconomicFcr(feedKg, netProductionKg + deadBiomassKg)
}

type DatedWeight = { date: string; weightG: number }

const dayOf = (log: { recordedAt: string; farmId: string }): string =>
  toLocalCalendarDayKey(log.recordedAt, farmTimeZone(log.farmId))

const groupByLotId = <T extends { lotId?: string }>(logs: readonly T[]): Map<string, T[]> => {
  const result = new Map<string, T[]>()
  for (const log of logs) {
    const lotId = typeof log.lotId === 'string' ? log.lotId.trim() : ''
    if (!lotId) continue
    const list = result.get(lotId)
    if (list) list.push(log)
    else result.set(lotId, [log])
  }
  return result
}

const weightSeries = (logs: readonly WeightSampleLog[]): DatedWeight[] => {
  const series: DatedWeight[] = []
  rollupWeightSamplesByLotDay(logs).forEach((rollup) => {
    const weightG = parseNumber(rollup.meanWeightGrams)
    if (rollup.date && weightG !== null && weightG > 0) series.push({ date: rollup.date, weightG })
  })
  return series.sort((a, b) => a.date.localeCompare(b.date))
}

/** Latest mean weight on or before the day; else the first one after it. */
const weightOn = (series: DatedWeight[], day: string): number | null => {
  let best: DatedWeight | undefined
  for (const point of series) {
    if (point.date <= day) best = point
    else break
  }
  return (best ?? series[0])?.weightG ?? null
}

export type LotFcrInputs = {
  /** First count × first mean weight of the lot (kg), or null without both. */
  stockedBiomassKg: number | null
  /** Σ deaths × mean weight at that day (kg) within the window; 0 with no deaths. */
  deadBiomassKg: number | null
  /** Deaths in the window. */
  deaths: number
}

/**
 * Per lot id: stocked biomass and the biomass of the fish that died inside
 * the optional `[fromDay, toDay]` window (farm calendar days, inclusive).
 * Only logs carrying a `lotId` count, so a deleted lot with the same name
 * never mixes in (findings #207). Dead biomass is null when there are deaths
 * but the lot has no weight sample at all.
 */
export const buildLotFcrInputs = (args: {
  weightSampleLogs: readonly WeightSampleLog[]
  mortalityLogs: readonly MortalityLog[]
  fishStockLogs: readonly FishStockLog[]
  fromDay?: string
  toDay?: string
}): Map<string, LotFcrInputs> => {
  const weightsByLot = groupByLotId(args.weightSampleLogs)
  const deathsByLot = groupByLotId(args.mortalityLogs)
  const countsByLot = groupByLotId(args.fishStockLogs)
  const lotIds = new Set([...weightsByLot.keys(), ...deathsByLot.keys(), ...countsByLot.keys()])
  const inWindow = (day: string): boolean =>
    Boolean(day) && (!args.fromDay || day >= args.fromDay) && (!args.toDay || day <= args.toDay)

  const result = new Map<string, LotFcrInputs>()
  lotIds.forEach((lotId) => {
    const series = weightSeries(weightsByLot.get(lotId) ?? [])

    const firstCount = (countsByLot.get(lotId) ?? [])
      .map((log) => ({ day: dayOf(log), count: parseNumber(log.countedStock) }))
      .filter((entry): entry is { day: string; count: number } => Boolean(entry.day) && entry.count !== null && entry.count > 0)
      .sort((a, b) => a.day.localeCompare(b.day))[0]
    const stockedBiomassKg = firstCount && series.length > 0
      ? (firstCount.count * (weightOn(series, firstCount.day) ?? 0)) / 1000
      : null

    let deaths = 0
    let deadGrams = 0
    for (const log of deathsByLot.get(lotId) ?? []) {
      const day = dayOf(log)
      if (!inWindow(day)) continue
      const count = parseNumber(log.mortalityCount)
      if (count === null || count <= 0) continue
      deaths += count
      deadGrams += count * (weightOn(series, day) ?? 0)
    }
    const deadBiomassKg = deaths > 0 && series.length === 0 ? null : deadGrams / 1000

    result.set(lotId, { stockedBiomassKg, deadBiomassKg, deaths })
  })
  return result
}
